import React, { useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { STORY_IMAGE_DIMENSIONS } from "../constants/imageConstants";

interface DefaultImageDisplayProps {
  title: string;
  width?: number;
  height?: number;
  textAlignment?: "left" | "center" | "right";
  sx?: any;
}

/**
 * Reusable component to display a default generated image with the story title.
 * This component generates the image on the client side using canvas.
 * Uses standard 3:4 aspect ratio (600x800) by default.
 * Features stylish typography with Manrope font, full-height text coverage,
 * and mixed font sizing for visual hierarchy.
 */
const DefaultImageDisplay: React.FC<DefaultImageDisplayProps> = ({
  title,
  width = STORY_IMAGE_DIMENSIONS.width,
  height = STORY_IMAGE_DIMENSIONS.height,
  textAlignment = "left",
  sx = {},
}) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width, height });
  const isDark = theme.palette.mode === "dark";

  // Measure container and update dimensions
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !title.trim()) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Use actual dimensions
    const actualWidth = Math.max(dimensions.width, 1);
    const actualHeight = Math.max(dimensions.height, 1);

    // Get device pixel ratio for crisp rendering on high-DPI displays
    const dpr = window.devicePixelRatio || 1;

    // Set canvas internal dimensions scaled by device pixel ratio
    canvas.width = Math.floor(actualWidth * dpr);
    canvas.height = Math.floor(actualHeight * dpr);

    // Scale the context to match device pixel ratio
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, actualWidth, actualHeight);

    const canvasWidth = actualWidth;
    const canvasHeight = actualHeight;

    // Create sophisticated multi-layer gradient background
    if (isDark) {
      const baseGradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
      baseGradient.addColorStop(0, "rgba(67, 56, 202, 0.6)");
      baseGradient.addColorStop(0.3, "rgba(99, 102, 241, 0.55)");
      baseGradient.addColorStop(0.6, "rgba(139, 92, 246, 0.5)");
      baseGradient.addColorStop(1, "rgba(79, 70, 229, 0.65)");

      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const radialGradient = ctx.createRadialGradient(
        canvasWidth * 0.2,
        canvasHeight * 0.2,
        0,
        canvasWidth * 0.2,
        canvasHeight * 0.2,
        Math.max(canvasWidth, canvasHeight) * 0.8
      );
      radialGradient.addColorStop(0, "rgba(167, 139, 250, 0.3)");
      radialGradient.addColorStop(0.5, "rgba(129, 140, 248, 0.2)");
      radialGradient.addColorStop(1, "rgba(0, 0, 0, 0.4)");
      ctx.fillStyle = radialGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const overlayGradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      overlayGradient.addColorStop(0, "rgba(0, 0, 0, 0.15)");
      overlayGradient.addColorStop(0.5, "rgba(0, 0, 0, 0.05)");
      overlayGradient.addColorStop(1, "rgba(0, 0, 0, 0.35)");
      ctx.fillStyle = overlayGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    } else {
      const baseGradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
      baseGradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
      baseGradient.addColorStop(0.3, "rgba(37, 99, 235, 0.6)");
      baseGradient.addColorStop(0.6, "rgba(29, 78, 216, 0.55)");
      baseGradient.addColorStop(1, "rgba(30, 64, 175, 0.65)");
      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const radialGradient = ctx.createRadialGradient(
        canvasWidth * 0.8,
        canvasHeight * 0.2,
        0,
        canvasWidth * 0.8,
        canvasHeight * 0.2,
        Math.max(canvasWidth, canvasHeight) * 0.9
      );
      radialGradient.addColorStop(0, "rgba(147, 197, 253, 0.4)");
      radialGradient.addColorStop(0.4, "rgba(96, 165, 250, 0.25)");
      radialGradient.addColorStop(1, "rgba(30, 64, 175, 0.3)");
      ctx.fillStyle = radialGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const overlayGradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      overlayGradient.addColorStop(0, "rgba(255, 255, 255, 0.15)");
      overlayGradient.addColorStop(0.5, "rgba(255, 255, 255, 0.05)");
      overlayGradient.addColorStop(1, "rgba(0, 0, 0, 0.15)");
      ctx.fillStyle = overlayGradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    // Typography settings
    const fontFamily = "'Playfair Display', 'Georgia', 'Times New Roman', serif";
    const textColor = "rgba(255, 255, 255, 1)";

    const horizontalPadding = canvasWidth * 0.1;
    const verticalPadding = canvasHeight * 0.15;
    const maxWidth = canvasWidth - (horizontalPadding * 2);
    const maxHeight = canvasHeight - (verticalPadding * 2);

    // --- Decorative Elements ---

    // Elegant Border Frame
    const drawBorder = () => {
      // Use logic consistent with utility/preview
      // Scale border inset/width roughly with canvas size if needed, but fixed 20px is good for >300px
      // For smaller displays, we scale it down
      const scale = Math.min(canvasWidth, canvasHeight) / 600;
      const borderInset = Math.max(10, 20 * scale);
      const borderWidth = Math.max(1, 2 * scale);

      ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
      ctx.lineWidth = borderWidth;

      // Outer line
      ctx.strokeRect(borderInset, borderInset, canvasWidth - (borderInset * 2), canvasHeight - (borderInset * 2));

      // Inner thin line
      ctx.lineWidth = borderWidth / 2;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
      const innerInset = borderInset + (6 * scale);
      ctx.strokeRect(innerInset, innerInset, canvasWidth - (innerInset * 2), canvasHeight - (innerInset * 2));
    };
    drawBorder();

    // --- Dynamic Text Sizing Logic ---
    const calculateLayout = (targetFontSize: number) => {
      ctx.font = `700 ${targetFontSize}px ${fontFamily}`;

      // 1. Check if any single word is too wide
      const words = title.split(" ");
      for (const word of words) {
        if (ctx.measureText(word).width > maxWidth) {
          return null; // Font too big for this word
        }
      }

      // 2. Wrap text
      const lines: string[] = [];
      let currentLine = words[0] || "";

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine + " " + word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) {
        lines.push(currentLine);
      }

      // 3. Check vertical fit
      const lineHeight = targetFontSize * 1.4;
      const totalHeight = lines.length * lineHeight;

      if (totalHeight > maxHeight) {
        return null; // Text too tall
      }

      return { lines, lineHeight, totalHeight };
    };

    // Find best font size
    let fontSize = Math.min(canvasWidth * 0.15, 80);
    const minFontSize = Math.max(10, canvasWidth * 0.05); // Scale min font size
    let layout = calculateLayout(fontSize);

    while (!layout && fontSize > minFontSize) {
      fontSize -= 2;
      layout = calculateLayout(fontSize);
    }

    if (!layout) {
      // Fallback for extremely long text
      layout = calculateLayout(minFontSize) || {
        lines: title.split(" "),
        lineHeight: minFontSize * 1.4,
        totalHeight: (title.split(" ").length) * minFontSize * 1.4
      };
    }

    const { lines, lineHeight, totalHeight } = layout;

    // Draw settings
    ctx.font = `700 ${fontSize}px ${fontFamily}`;
    ctx.textAlign = textAlignment;
    ctx.textBaseline = "middle";

    // Position calculation
    const getTextX = (): number => {
      switch (textAlignment) {
        case "left": return horizontalPadding;
        case "right": return canvasWidth - horizontalPadding;
        case "center": default: return canvasWidth / 2;
      }
    };

    const textX = getTextX();
    const centerY = canvasHeight / 2;
    const startY = centerY - (totalHeight / 2) + (lineHeight / 2);

    // Draw lines
    lines.forEach((line, index) => {
      const y = startY + (index * lineHeight);

      const shadowOffsetX = textAlignment === "left" ? 2 : textAlignment === "right" ? -2 : 0;

      // Shadows
      ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
      ctx.fillText(line, textX + (shadowOffsetX > 0 ? 1 : shadowOffsetX < 0 ? -1 : 0), y + 1);

      // Main Text
      ctx.shadowColor = isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.2)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = textColor;
      ctx.fillText(line, textX, y);

      ctx.shadowColor = "transparent";
    });
  }, [title, theme, textAlignment, dimensions.width, dimensions.height, isDark]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          maxWidth: "100%",
          maxHeight: "100%",
          transition: "transform 0.4s ease",
        }}
      />
    </Box>
  );
};

export default DefaultImageDisplay;
